import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Film, Users, MapPin, ArrowRight, Star, Sparkles, LogIn, Mail, Facebook, Twitter, Instagram, Linkedin, Quote, User, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Mock data - moved outside component to prevent recreation
const states = [
  'Maharashtra',
  'Delhi',
  'Karnataka',
  'Tamil Nadu',
  'West Bengal',
  'Telangana',
  'Rajasthan',
  'Kerala'
];

const cityData = {
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik'],
  'Delhi': ['New Delhi', 'Noida', 'Gurgaon', 'Faridabad'],
  'Karnataka': ['Bangalore', 'Mysore', 'Mangalore', 'Hubli'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar'],
  'Rajasthan': ['Jaipur', 'Udaipur', 'Jodhpur', 'Kota'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur']
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, login, logout } = useAuth();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);

  // Stats moved inside component (uses state hooks)

  const stats = [
    { label: 'Film Professionals', value: '50K+', icon: Users },
    { label: 'Active Projects', value: '2.5K+', icon: Film },
    { label: 'Cities Covered', value: '100+', icon: MapPin },
    { label: 'Success Stories', value: '10K+', icon: Star }
  ];

  const features = [
    {
      title: 'Location-Based Search',
      description: 'Find film professionals in your city with precision location filtering',
      icon: MapPin,
      image: 'https://images.unsplash.com/photo-1619468129361-605ebea04b44',
      color: 'from-orange-500/10 to-red-500/10'
    },
    {
      title: 'Department Categories',
      description: 'Browse through 15+ specialized departments from art to VFX',
      icon: Film,
      image: 'https://images.unsplash.com/photo-1518930259200-3e5b29f42096',
      color: 'from-blue-500/10 to-cyan-500/10'
    },
    {
      title: 'Verified Professionals',
      description: 'Connect with verified crew members and industry experts',
      icon: Star,
      image: 'https://images.unsplash.com/photo-1706696950760-4ee1d997bb51',
      color: 'from-green-500/10 to-emerald-500/10'
    },
    {
      title: 'Community Network',
      description: 'Join a thriving community of passionate film creators',
      icon: Users,
      image: 'https://images.unsplash.com/photo-1563461661026-49631dd5d68e',
      color: 'from-purple-500/10 to-pink-500/10'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Cinematographer',
      department: 'Camera Department',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a',
      quote: 'Shoot Base helped me connect with amazing production houses across India. Found my dream project within weeks!'
    },
    {
      name: 'Priya Sharma',
      role: 'Art Director',
      department: 'Art Department',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2',
      quote: 'The platform made it so easy to showcase my portfolio and get hired for multiple feature films. Highly recommended!'
    },
    {
      name: 'Arjun Menon',
      role: 'Sound Engineer',
      department: 'Sound Department',
      image: 'https://images.pexels.com/photos/29852852/pexels-photo-29852852.jpeg',
      quote: 'As a freelancer, Shoot Base became my go-to platform for finding quality projects. The community is incredibly supportive.'
    }
  ];

  const departments = [
    'Art Department',
    'Camera Department',
    'Sound Department',
    'Direction',
    'Makeup & Hair',
    'SFX / VFX',
    'Production',
    'Assistant Directors',
    'Lighting Department',
    'Post Production',
    'Script Department',
    'Editing'
  ];

  // Mock professional count by city
  const cityProfessionalCount = {
    'Mumbai': 12847,
    'Pune': 3421,
    'New Delhi': 8932,
    'Noida': 4567,
    'Bangalore': 6789,
    'Chennai': 5432,
    'Kolkata': 4321,
    'Hyderabad': 7890
  };

  useEffect(() => {
    if (selectedState) {
      setCities(cityData[selectedState] || []);
      setSelectedCity('');
    }
  }, [selectedState]);

  const handleExplore = () => {
    if (!selectedState || !selectedCity) {
      toast.error('Please select both state and city to continue');
      return;
    }
    toast.success(`Exploring ${selectedCity}, ${selectedState}`);
    navigate(`/departments/${selectedState}/${selectedCity}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-4 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
            >
              <img src="/logo.png" alt="Shoot Base" className="h-16 md:h-20 w-auto object-contain" />
              <h1 className="text-2xl md:text-3xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                Shoot Base
              </h1>
            </button>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <Button variant="ghost" className="hidden md:inline-flex hover:text-accent">About</Button>
              <Button variant="ghost" className="hidden md:inline-flex hover:text-accent" onClick={() => navigate('/discover')}>Discover Projects</Button>
              <Button variant="ghost" className="hidden md:inline-flex hover:text-accent" onClick={() => navigate('/departments')}>Departments</Button>
              <Button variant="ghost" className="hidden md:inline-flex hover:text-accent" onClick={() => navigate('/post-project')}>Create a Hiring Post</Button>
              
              {isAuthenticated && user ? (
                <>
                  <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">{user.name}</span>
                  </div>
                  <Button 
                    variant="ghost"
                    size="sm" 
                    className="hidden md:inline-flex hover:text-accent gap-2"
                    onClick={logout}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" className="hidden md:inline-flex hover:text-accent gap-2" onClick={login}>
                    <LogIn className="w-4 h-4" />
                    Sign in with Google
                  </Button>
                  <Button className="bg-accent hover:bg-accent/90 text-primary-foreground" onClick={login}>Join Now</Button>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        {/* Hero Background Image with Enhanced Overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1612544409025-e1f6a56c1152)',
              filter: 'brightness(0.4) blur(2px)'
            }}
          />
          {/* Multi-layer gradient overlay for perfect text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white/90"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-transparent to-white/80"></div>
          
          {/* Subtle decorative elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-accent/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center max-w-4xl mx-auto mb-10"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Badge className="mb-3 bg-accent/90 text-white border-0 shadow-lg">
                <Sparkles className="w-3 h-3 mr-1" />
                Welcome to Shoot Base
              </Badge>
            </motion.div>
            <motion.h2 
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <span className="bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent drop-shadow-sm">
                Source. Connect. Create.
              </span>
            </motion.h2>
            <motion.p 
              className="text-base md:text-lg text-foreground/80 font-medium mb-6 max-w-2xl mx-auto drop-shadow-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Your gateway to India's largest network of film professionals. Find talent, build teams, and create magic together.
            </motion.p>
          </motion.div>

          {/* Location Selector with enhanced animation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-card/98 backdrop-blur-xl border-border shadow-2xl ring-1 ring-accent/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-accent" />
                    <h3 className="text-lg font-semibold text-foreground">Select Your Location</h3>
                  </div>
                  {selectedCity && cityProfessionalCount[selectedCity] && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                        <Users className="w-3 h-3 mr-1" />
                        {cityProfessionalCount[selectedCity].toLocaleString()} professionals
                      </Badge>
                    </motion.div>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">State</label>
                    <Select value={selectedState} onValueChange={setSelectedState}>
                      <SelectTrigger className="w-full bg-background border-border">
                        <SelectValue placeholder="Choose your state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">City</label>
                    <Select value={selectedCity} onValueChange={setSelectedCity} disabled={!selectedState}>
                      <SelectTrigger className="w-full bg-background border-border">
                        <SelectValue placeholder="Choose your city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={handleExplore}
                    className="flex-1 bg-accent hover:bg-accent/90 text-primary-foreground font-semibold group shadow-lg hover:shadow-xl transition-all"
                    size="lg"
                  >
                    Explore Departments
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    onClick={() => navigate('/post-project')}
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent hover:text-white"
                    size="lg"
                  >
                    Post a Job
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-b from-background to-orange-ultralight">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-card backdrop-blur border-border hover:border-accent/50 transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-6 text-center">
                    <stat.icon className="w-8 h-8 mx-auto mb-3 text-accent" />
                    <h3 className="text-3xl font-bold mb-1 text-foreground">{stat.value}</h3>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-3 text-foreground">Why Choose Shoot Base?</h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              The most comprehensive platform for film industry professionals in India
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full bg-gradient-to-br ${feature.color} backdrop-blur border-border hover:border-accent/50 transition-all duration-300 group hover:shadow-lg overflow-hidden`}>
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={feature.image} 
                        alt={feature.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-6 bg-accent/90 w-12 h-12 rounded-xl flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold mb-2 text-foreground">{feature.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials / Success Stories Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <Badge className="mb-3 bg-accent/10 text-accent border-accent/30">
              <Star className="w-3 h-3 mr-1" />
              Success Stories
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-3 text-foreground">Loved by Film Professionals</h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Hear from crew members who found their dream projects through Shoot Base
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full bg-card border-border hover:border-accent/50 transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 text-accent/30 mb-4" />
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6 italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-4">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-accent/20"
                      />
                      <div>
                        <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                        <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                        <Badge className="mt-1 text-xs bg-accent/10 text-accent border-accent/30">
                          {testimonial.department}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-foreground">Ready to Get Started?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of film professionals who trust Shoot Base for their sourcing needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold shadow-lg"
                onClick={() => navigate('/auth')}
              >
                Join as Professional
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-accent text-accent hover:bg-accent hover:text-primary-foreground shadow-lg"
                onClick={() => navigate('/post-project')}
              >
                Create a Hiring Post
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={() => navigate('/')}>
                <img src="/logo.png" alt="Shoot Base" className="h-12 w-auto object-contain" />
                <h3 className="text-lg font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>Shoot Base</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Built by film professionals, for film professionals. Your one-stop platform to source, connect, and create.
              </p>
              <Button 
                className="bg-accent hover:bg-accent/90 text-primary-foreground"
                onClick={() => navigate('/contribute')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Contribute Vendor Data
              </Button>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Platform</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => navigate('/about')} className="hover:text-accent transition-colors">About Us</button></li>
                <li><button onClick={() => navigate('/how-it-works')} className="hover:text-accent transition-colors">How it Works</button></li>
                <li><button onClick={() => navigate('/departments')} className="hover:text-accent transition-colors">Departments</button></li>
                <li><button onClick={() => navigate('/success-stories')} className="hover:text-accent transition-colors">Success Stories</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => navigate('/help')} className="hover:text-accent transition-colors">Help Center</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-accent transition-colors">Contact Us</button></li>
                <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-accent transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms-of-service')} className="hover:text-accent transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>© 2024 Shoot Base. Built with passion for the film industry.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;